import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { parseList, parseMockRoute, parseSeedCookie, parseStringRecord } from "../core/context";
import {
  DECOY_PRESETS,
  DEVICE_PROFILES,
  decoyPresetOf,
  KNOWN_DECOYS,
} from "../core/environments/browser/profiles";
import {
  type ContextSettings,
  defaultSettings,
  fromDateTimeInput,
  parseJsonField,
  toDateTimeInput,
} from "../core/environments/browser/settings";

const TIME_ZONES = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
];

const field =
  "w-full rounded border border-border bg-bg px-2 py-1 font-mono text-xs text-fg focus-visible:outline-2 focus-visible:outline-blue";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-2 border-b border-border/60 px-4 py-3">
      <legend className="text-xs font-semibold uppercase tracking-wide text-fg-dark">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Labeled({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs text-fg-dark">
        {label}
      </label>
      {children(id)}
    </div>
  );
}

/** A JSON textarea that only commits values that validate. */
function JsonField<T>({
  label,
  testId,
  value,
  parse,
  onChange,
  rows = 4,
}: {
  label: string;
  testId: string;
  value: T;
  parse: (v: unknown) => T | null;
  onChange: (value: T) => void;
  rows?: number;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);
  const committed = useRef(value);
  // Follow outside changes (picking an example, Reset), not our own edits.
  useEffect(() => {
    if (value === committed.current) return;
    committed.current = value;
    setText(JSON.stringify(value, null, 2));
    setError(null);
  }, [value]);
  return (
    <Labeled label={label}>
      {(id) => (
        <>
          <textarea
            id={id}
            data-testid={testId}
            rows={rows}
            value={text}
            spellCheck={false}
            aria-invalid={error !== null}
            onChange={(event) => {
              setText(event.target.value);
              const result = parseJsonField(event.target.value, parse);
              if ("value" in result) {
                setError(null);
                committed.current = result.value;
                onChange(result.value);
              } else setError(result.error);
            }}
            className={field}
          />
          {error && (
            <p role="alert" className="text-xs text-red1">
              {error}
            </p>
          )}
        </>
      )}
    </Labeled>
  );
}

const parseCookies = (v: unknown) => parseList(v, parseSeedCookie);
const parseRoutes = (v: unknown) => parseList(v, parseMockRoute);

/**
 * The Simulated Context drawer (docs/ui-design.md §2, ADR-0017): the page,
 * date, browser identity, decoys, seeded storage, and mock routes the next
 * run uses.
 */
export function ContextDrawer({
  settings,
  onChange,
  onClose,
}: {
  settings: ContextSettings;
  onChange: (settings: ContextSettings) => void;
  onClose: () => void;
}) {
  const { context } = settings;
  const set = (patch: Partial<ContextSettings["context"]>) =>
    onChange({ ...settings, context: { ...context, ...patch } });
  const deviceId =
    Object.entries(DEVICE_PROFILES).find(
      ([, p]) => JSON.stringify(p.device) === JSON.stringify(context.device),
    )?.[0] ?? "custom";
  const preset = decoyPresetOf(context.decoys) ?? "custom";
  const fill = context.activity?.fill ?? false;
  const submit = fill && (context.activity?.submit ?? false);
  const setActivity = (next: { fill: boolean; submit: boolean }) => {
    const { activity, ...rest } = context;
    onChange({
      ...settings,
      context: next.fill ? { ...rest, activity: { ...activity, ...next } } : rest,
    });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside
      aria-label="Simulated context"
      data-testid="context-drawer"
      className="fixed inset-y-0 right-0 z-20 flex w-full max-w-md flex-col border-l border-border bg-bg-dark shadow-2xl"
    >
      <header className="flex items-center gap-2 border-b border-border px-4 py-2">
        <h2 className="font-semibold">Simulated context</h2>
        <button
          type="button"
          onClick={() => onChange(defaultSettings())}
          className="ml-auto rounded border border-border px-2 py-0.5 text-xs text-fg-dark hover:text-fg"
        >
          Reset to defaults
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the context drawer"
          className="rounded p-1 text-fg-dark hover:text-fg"
        >
          <X size={16} aria-hidden />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section title="Page">
          <Labeled label="URL">
            {(id) => (
              <input
                id={id}
                className={field}
                value={context.url}
                onChange={(e) => set({ url: e.target.value })}
              />
            )}
          </Labeled>
          <Labeled label="Referrer">
            {(id) => (
              <input
                id={id}
                className={field}
                value={context.referrer}
                onChange={(e) => set({ referrer: e.target.value })}
              />
            )}
          </Labeled>
        </Section>

        <Section title="Decoy globals">
          <Labeled label="Preset">
            {(id) => (
              <select
                id={id}
                data-testid="decoy-preset"
                className={field}
                value={preset}
                onChange={(e) => {
                  const next = DECOY_PRESETS[e.target.value];
                  if (next) set({ decoys: [...next.decoys] });
                }}
              >
                {Object.entries(DECOY_PRESETS).map(([id, p]) => (
                  <option key={id} value={id}>
                    {p.label}
                  </option>
                ))}
                {preset === "custom" && <option value="custom">Custom</option>}
              </select>
            )}
          </Labeled>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {KNOWN_DECOYS.map((name) => (
              <label key={name} className="flex items-center gap-1 font-mono text-xs">
                <input
                  type="checkbox"
                  checked={context.decoys.includes(name)}
                  onChange={(e) =>
                    set({
                      decoys: e.target.checked
                        ? [...context.decoys, name]
                        : context.decoys.filter((d) => d !== name),
                    })
                  }
                />
                {name}
              </label>
            ))}
          </div>
        </Section>

        <Section title="Time and randomness">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={settings.now === null}
              onChange={(e) =>
                onChange({
                  ...settings,
                  now: e.target.checked ? null : Date.now() - (Date.now() % 60_000),
                })
              }
            />
            Use the real time when the run starts
          </label>
          {settings.now !== null && (
            <Labeled label="Date (UTC)">
              {(id) => (
                <input
                  id={id}
                  type="datetime-local"
                  className={field}
                  value={toDateTimeInput(settings.now ?? 0)}
                  onChange={(e) => {
                    const ms = fromDateTimeInput(e.target.value);
                    if (ms !== null) onChange({ ...settings, now: ms });
                  }}
                />
              )}
            </Labeled>
          )}
          <Labeled label="Time zone (what Intl reports)">
            {(id) => (
              <select
                id={id}
                className={field}
                value={context.timezone}
                onChange={(e) => set({ timezone: e.target.value })}
              >
                {[...new Set([context.timezone, ...TIME_ZONES])].map((tz) => (
                  <option key={tz}>{tz}</option>
                ))}
              </select>
            )}
          </Labeled>
          <Labeled label="Seed">
            {(id) => (
              <input
                id={id}
                type="number"
                className={field}
                value={settings.seed}
                onChange={(e) => {
                  const seed = Number(e.target.value);
                  if (Number.isSafeInteger(seed)) onChange({ ...settings, seed });
                }}
              />
            )}
          </Labeled>
        </Section>

        <Section title="Browser">
          <Labeled label="Device">
            {(id) => (
              <select
                id={id}
                className={field}
                value={deviceId}
                onChange={(e) => {
                  const p = DEVICE_PROFILES[e.target.value];
                  if (p) set({ device: structuredClone(p.device) });
                }}
              >
                {Object.entries(DEVICE_PROFILES).map(([id, p]) => (
                  <option key={id} value={id}>
                    {p.label}
                  </option>
                ))}
                {deviceId === "custom" && <option value="custom">Custom</option>}
              </select>
            )}
          </Labeled>
          <Labeled label="Languages (comma-separated)">
            {(id) => (
              <input
                id={id}
                className={field}
                value={context.languages.join(", ")}
                onChange={(e) => {
                  const languages = e.target.value
                    .split(",")
                    .map((l) => l.trim())
                    .filter(Boolean);
                  if (languages.length > 0) set({ languages });
                }}
              />
            )}
          </Labeled>
        </Section>

        <Section title="Storage">
          <JsonField
            label="Cookies"
            testId="context-cookies"
            value={context.cookies}
            parse={parseCookies}
            onChange={(cookies) => set({ cookies })}
          />
          <JsonField
            label="localStorage"
            testId="context-local-storage"
            value={context.localStorage}
            parse={parseStringRecord}
            onChange={(localStorage) => set({ localStorage })}
          />
          <JsonField
            label="sessionStorage"
            testId="context-session-storage"
            value={context.sessionStorage}
            parse={parseStringRecord}
            onChange={(sessionStorage) => set({ sessionStorage })}
            rows={2}
          />
        </Section>

        <Section title="User activity">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="context-activity-fill"
              checked={fill}
              onChange={(e) => setActivity({ fill: e.target.checked, submit })}
            />
            After load, type into the page's card, password, and contact fields
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="context-activity-submit"
              checked={submit}
              disabled={!fill}
              onChange={(e) => setActivity({ fill, submit: e.target.checked })}
            />
            Then submit the form
          </label>
        </Section>

        <Section title="Network">
          <JsonField
            label="Mock routes (first match wins; * is a wildcard)"
            testId="context-mock-routes"
            value={context.mockRoutes}
            parse={parseRoutes}
            onChange={(mockRoutes) => set({ mockRoutes })}
          />
        </Section>

        <Section title="Initial HTML">
          <Labeled label="Document">
            {(id) => (
              <textarea
                id={id}
                rows={8}
                spellCheck={false}
                className={field}
                value={context.html}
                onChange={(e) => set({ html: e.target.value })}
              />
            )}
          </Labeled>
        </Section>
      </div>
    </aside>
  );
}
